'use client'

import { useState } from 'react'

import { trackNewsletterSubscription } from '@/lib/c2c/newsletter/track-subscription'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type TrackFn = typeof trackNewsletterSubscription

interface NewsletterFormProps {
    locale: string
    /** Injected in tests; defaults to the real tracking pipeline. */
    trackFn?: TrackFn
}

export function NewsletterForm({
    locale,
    trackFn = trackNewsletterSubscription,
}: NewsletterFormProps) {
    const [email, setEmail] = useState('')
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
    const [validationError, setValidationError] = useState<string | null>(null)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setValidationError(null)

        if (!EMAIL_RE.test(email)) {
            setValidationError('Please enter a valid email.')
            return
        }

        setStatus('submitting')
        const result = await trackFn({
            email,
            locale,
            originCity: 'any',
            destinationCity: 'any',
            transportMode: 'any',
        })
        setStatus(result.ok ? 'success' : 'error')
    }

    const isSubscribed = status === 'success'
    const isBusy = status === 'submitting' || isSubscribed

    return (
        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
            <div className="flex items-stretch overflow-hidden rounded-full border border-[#D9DEE5] bg-white transition-all focus-within:border-[#00BFD5] focus-within:shadow-[0_0_0_3px_rgba(0,191,213,0.18)]">
                <input
                    type="email"
                    placeholder="your@email.com"
                    aria-label="Email address"
                    required
                    disabled={isBusy}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="min-w-0 flex-1 border-0 bg-transparent px-3 py-2 text-[13px] text-[#2E3033] outline-none placeholder:text-[#8F98A6] disabled:opacity-70"
                />
                <button
                    type="submit"
                    disabled={isBusy}
                    className="cursor-pointer border-0 bg-[#00BFD5] px-4 text-[12px] font-bold tracking-[0.04em] text-white transition-colors hover:bg-[#009CAD] disabled:cursor-default disabled:opacity-50"
                >
                    {isSubscribed ? 'Subscribed!' : 'Subscribe'}
                </button>
            </div>
            {validationError && (
                <p className="mt-1.5 text-xs text-red-600" role="alert">
                    {validationError}
                </p>
            )}
            {status === 'error' && (
                <p className="mt-1.5 text-xs text-red-600" role="alert">
                    Something went wrong. Please try again.
                </p>
            )}
        </form>
    )
}
